import { z } from "zod";
import { ShortText, MediumText, idString } from "./common.js";
/**
 * The published params shape BEFORE the KPI Manager binding — a tile that
 * carried its own value, target and sparkline series.
 *
 * Retained for two separate reasons, and neither is decoration. Player AI
 * catalogues V3–V11 embed this exact body and are resolved by EXACT checksum,
 * so it must stay byte-identical; and it is the INPUT half of
 * {@link migrateKpiTileParams}, which is how an existing manually populated
 * tile keeps its values instead of being discarded.
 */
export const RetiredKpiTileParamsV1Schema = z
    .object({
    label: ShortText,
    value: ShortText,
    unit: ShortText.optional(),
    target: z.number().finite().optional(),
    higherIsBetter: z.boolean(),
    recentValues: z.array(z.number().finite()).max(200),
    sourceText: MediumText.optional(),
})
    .strict();
/**
 * The observation windows the tile may show. These are KPI Manager's OWN
 * ranges (`KPI_OBSERVATION_RANGES` in `@playbookai/kpi-feature`), reused rather
 * than re-invented: the tile must not be able to present a window the KPI's own
 * page cannot, or the two surfaces would disagree about the same measure.
 */
export const KPI_TILE_RANGES = ["30d", "90d", "all"];
export const KpiTileRangeSchema = z.enum(KPI_TILE_RANGES);
export const DEFAULT_KPI_TILE_RANGE = "90d";
/**
 * What a pre-binding tile said, preserved verbatim.
 *
 * FROZEN by construction: no editor writes it, the merge map does not address
 * it, and a tile that names a `kpiId` may not carry it at all (see the refinement
 * below). It exists so the migration is non-destructive — the numbers somebody
 * typed stay readable until they choose the KPI that replaces them.
 */
export const KpiTileLegacyDataSchema = z
    .object({
    value: ShortText,
    unit: ShortText.optional(),
    target: z.number().finite().optional(),
    higherIsBetter: z.boolean(),
    recentValues: z.array(z.number().finite()).max(200),
    sourceText: MediumText.optional(),
})
    .strict();
const KpiTileParamsObjectSchema = z
    .object({
    /**
     * The KPI Manager KPI this tile displays. Absent means "not linked yet",
     * which is a real state: an unlinked tile shows a chooser (and, after the
     * migration, whatever it used to say), never a zero.
     */
    kpiId: idString.optional(),
    /**
     * Caption override. PRESENTATION — blank falls back to the KPI's own name,
     * so a tile never has to restate what KPI Manager already knows.
     */
    label: ShortText,
    /** Which observation window the trend covers. PRESENTATION. */
    range: KpiTileRangeSchema.optional(),
    /** Preserved pre-binding values; see {@link KpiTileLegacyDataSchema}. */
    legacy: KpiTileLegacyDataSchema.optional(),
})
    .strict()
    .superRefine((params, ctx) => {
    if (params.kpiId !== undefined && params.legacy !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["legacy"],
            message: "A linked KPI Tile carries no manual values; KPI Manager is the authority.",
        });
    }
});
/**
 * Fold a pre-binding tile into the current shape.
 *
 * Pure, total and IDEMPOTENT: anything that is not exactly the retired shape is
 * returned untouched, so the current shape, a malformed row and a partially
 * migrated row all pass straight through to the validator that called us.
 *
 * This is deliberately the WHOLE migration. It runs wherever params are
 * validated — the canvas read seam (`CanvasWidget` parses before rendering),
 * every editor commit, and Playbook BE's own `validateWidgetParams` — so there
 * is no batch job to schedule, nothing to run twice, and no window in which a
 * row is readable by one half of the product and not the other. It also cannot
 * silently create a KPI Manager record, because it never talks to one: the
 * numbers move into `legacy` and wait for a person to choose the KPI.
 */
export function migrateKpiTileParams(raw) {
    const retired = RetiredKpiTileParamsV1Schema.safeParse(raw);
    if (!retired.success)
        return raw;
    const { label, ...legacy } = retired.data;
    return { label, legacy };
}
/**
 * KPI Tile — Specify, multi (addable). A READ-ONLY view of one KPI Manager KPI.
 *
 * The tile stores the LINK and how to present it, never the measurement. Name,
 * unit, current value, applicable target and history are resolved from KPI
 * Manager at render time, so a tile can no longer disagree with the KPI it
 * claims to show, and no tile-side write path can move a governed number.
 */
export const KpiTileParamsSchema = z.preprocess(migrateKpiTileParams, KpiTileParamsObjectSchema);
export const defaultKpiTileParams = {
    label: "",
    range: DEFAULT_KPI_TILE_RANGE,
};
//# sourceMappingURL=kpiTile.js.map